import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import type {
  ColumnSizingState,
  OnChangeFn,
  RowSelectionState,
} from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import type {
  BulkToolbarContext,
  ColumnLayout,
  DataTableProps,
  FilterValue,
  ResponseMeta,
  TableQueryState,
  TableRowData,
} from '../types';
import { Table } from '@/components/ui/table';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';
import { TableFooter } from './TableFooter';
import { DataTableToolbar } from './DataTableToolbar';
import { ColumnsPanel, type ColumnsPanelItem } from './ColumnsPanel';
import { OverflowIndicator } from './OverflowIndicator';
import { ErrorFallback } from '@/components/shared/placeholders/Error';
import { STICKY_ABOVE_APP_FOOTER } from '@/components/footerLayout';
import { useDataTableQuery } from '../hooks/useDataTableQuery';
import { useDataTableState } from '../hooks/useDataTableState';
import { useDataTableInstance } from '../hooks/useDataTableInstance';
import { useDataTableStateActions } from '../hooks/useDataTableStateActions';
import { useDataTableActiveFilters } from '../hooks/useDataTableActiveFilters';
import { useDataTableStateEncoding } from '../hooks/useDataTableStateEncoding';
import { useDataTableLocalPreferences } from '../hooks/useDataTableLocalPreferences';
import { extractValidSortFields } from '../utils/statePersistence';
import { getLeafColumns } from '../utils/columnGroupUtils';
import {
  applyColumnLayout,
  DEFAULT_COLUMN_LAYOUT,
  getCustomizableColumns,
  mergeColumnOrder,
  moveItem,
  resolveColumnOrder,
} from '../utils/columnLayout';
import { reconcileSelectionWithData } from '../utils/selectionReconcile';
import {
  getFixedLayoutMinWidth,
  isFillColumn,
  readColumnSize,
} from '../utils/columnSizing';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  validateColumnsOperators,
  filterValidFilters,
  hydrateWithValidFilters,
} from '../utils/columnValidation';
import {
  DEFAULT_PAGE_SIZE_OPTIONS,
  DEFAULT_TABLE_FEATURES,
} from '../constants';
import { DataTableSearchContext } from '../DataTableSearchContext';

/**
 * Remove banned values from filters. Returns the same array when nothing
 * changed, so a render-time `setFilters` does not loop.
 */
function dropBannedFilterValues(
  filters: FilterValue[],
  dropped: ReadonlyArray<{ field: string; value: string }> | undefined
): FilterValue[] {
  if (!dropped || dropped.length === 0) return filters;
  let changed = false;
  const next = filters.flatMap(filter => {
    const banned = dropped
      .filter(entry => entry.field === filter.field)
      .map(entry => entry.value);
    if (banned.length === 0) return [filter];
    const value = filter.value.filter(item => !banned.includes(item));
    if (value.length === filter.value.length) return [filter];
    changed = true;
    if (value.length === 0) return [];
    return [{ ...filter, value }];
  });
  return changed ? next : filters;
}

/** Width of the row-expansion chevron column (`w-10`). */
const EXPANSION_COLUMN_WIDTH = 40;
/** Width of that column with a `rowExpansion.toggleAddon` beside the chevron (`w-20`). */
const EXPANSION_COLUMN_WITH_ADDON_WIDTH = 80;
const BULK_BAR_CLASS =
  'mx-auto w-fit max-w-full rounded-lg shadow-lg empty:hidden';

/** Stable "no saved widths", so a layout without widths is not a new object per render. */
const NO_WIDTHS: ColumnSizingState = {};

/**
 * All-in-one DataTable component with built-in query state management.
 * Use this for quick setup with server-side pagination and filtering.
 *
 * For more control, use DataTableStateProvider + useDataTableQuery + DataTable separately.
 */
function DataTableInner<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
>({
  id,
  columns,
  paginationOptions,
  initialState,
  noDataLabel,
  getQueryKeysFromParams,
  queryFn,
  queryConfig,
  onMeta,
  countScope,
  className,
  containerClassName,
  tableClassName,
  tableLayout = 'fixed',
  features,
  rowExpansion,
  onRowClick,
  getRowClassName,
  search,
  rowSelection: rowSelectionOptions,
  toolbarEndSlot,
  onClearFilters,
  clearFiltersUrlKeys,
  pageResetKey,
  droppedFilterValues,
  columnCustomization,
}: DataTableProps<TRow, TMeta>) {
  const enableRowSelection = Boolean(rowSelectionOptions);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // "Select all matching across pages" mode. While true, `rowSelection` is
  // repurposed as the EXCLUSION set (keys removed from the all-matching set).
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const pageSizeOptions =
    paginationOptions?.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;
  const { state, dispatch } = useDataTableState({ initialState });
  const actions = useDataTableStateActions(dispatch, state.filters);
  // Out-of-band scope change (pageResetKey) → back to page 1. Adjusted during
  // render (not in an effect) so no request goes out for the stale page.
  const [prevPageResetKey, setPrevPageResetKey] = useState(pageResetKey);
  if (prevPageResetKey !== pageResetKey) {
    setPrevPageResetKey(pageResetKey);
    if (state.pagination.pageIndex !== 0) actions.setPage(0);
  }
  // Same render-time adjustment as pageResetKey: a URL that still names a
  // dropped value must not go out as a request.
  const keptFilters = dropBannedFilterValues(
    state.filters,
    droppedFilterValues
  );
  if (keptFilters !== state.filters) actions.setFilters(keptFilters);
  const activeFilters = useDataTableActiveFilters(keptFilters);
  // Columns flagged `hidden` still drive filtering / URL-state / persistence
  // (the full `columns` array below), but must not be rendered as a header or
  // body cell. `renderColumns` is the visible subset handed to the table
  // instance + header (KIR-3769 back-compat `type` filter behind `title`).
  //
  // KIR-4089: a user column layout reorders the columns and marks user-hidden
  // ones `hidden`, so they drop out of `renderColumns` the same way.
  // Columns the table is filtered or sorted by stay visible even if the
  // layout hides them, so the active filter/sort keeps its header controls.
  //
  // `renderColumns` must keep its identity across filter/sort changes: a new
  // array rebuilds every cell renderer and remounts all body cells. So the
  // forced set is reduced to a string key of the ids it actually un-hides
  // (always '' for tables without a layout).
  const columnLayout = columnCustomization?.layout;
  const onLayoutChange = columnCustomization?.onLayoutChange;
  const updateLayout = useCallback(
    (next: Partial<ColumnLayout>): void => {
      if (!onLayoutChange || !columnLayout) return;
      onLayoutChange({ ...columnLayout, ...next });
    },
    [onLayoutChange, columnLayout]
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Resizing and header drag need somewhere to keep the widths (the
  // layout) and a layout that pins them (`fixed`; `auto` sizes uncapped
  // columns to 100%).
  const resizableColumns =
    Boolean(columnCustomization) && tableLayout === 'fixed';
  // Column widths live in the layout (controlled) when the table is
  // customizable. The resize strip clamps a width, and drops an unchanged
  // one, before it gets here.
  const handleColumnSizingChange = useCallback<OnChangeFn<ColumnSizingState>>(
    updater => {
      const current = columnLayout?.widths ?? NO_WIDTHS;
      updateLayout({
        widths: typeof updater === 'function' ? updater(current) : updater,
      });
    },
    [columnLayout, updateLayout]
  );
  // Keyed on the order and hidden lists, not the whole layout: a width
  // change makes a new layout object but must not remount the body cells.
  // The preferences cache keeps unchanged lists as the same arrays.
  const layoutOrder = columnLayout?.order;
  const layoutHidden = columnLayout?.hidden;
  const forcedVisibleKey = useMemo(() => {
    if (!layoutHidden || layoutHidden.length === 0) return '';
    const active = new Set(state.filters.map(filter => filter.field));
    // Resolve the sorted column among rendered-capable columns only, so a
    // definition-hidden column sharing the sort field can't shadow it.
    const sortedColumn = columns.find(
      column =>
        !column.hidden && (column.sortField ?? column.id) === state.sort?.field
    );
    if (sortedColumn) active.add(sortedColumn.id);
    return layoutHidden
      .filter(id => active.has(id))
      .sort()
      .join(',');
  }, [columns, layoutHidden, state.filters, state.sort]);
  const renderColumns = useMemo(
    () =>
      (layoutOrder && layoutHidden
        ? applyColumnLayout(
            columns,
            { order: layoutOrder, hidden: layoutHidden },
            new Set(forcedVisibleKey ? forcedVisibleKey.split(',') : [])
          )
        : columns
      ).filter(column => !column.hidden),
    [columns, layoutOrder, layoutHidden, forcedVisibleKey]
  );
  const mergedFeatures = {
    ...DEFAULT_TABLE_FEATURES,
    ...features,
  };
  const enableRowExpansion = Boolean(mergedFeatures.enableRowExpansion);
  const enableSorting = mergedFeatures.enableSorting;
  const showToolbar = mergedFeatures.showToolbar;
  const showCardWrapper = mergedFeatures.showCardWrapper;
  const showShadow = mergedFeatures.showShadow;
  const enableLocalPreferences = mergedFeatures.enableLocalPreferences;
  const enableUrlState = mergedFeatures.enableUrlState;
  const debouncedSearch = useDebouncedValue(state.search, 300);
  const normalizedSearch = debouncedSearch.trim();
  const querySearch = normalizedSearch || undefined;

  const onSearchRef = useRef(search?.onSearch);
  useEffect(() => {
    onSearchRef.current = search?.onSearch;
  });
  useEffect(() => {
    onSearchRef.current?.(normalizedSearch);
  }, [normalizedSearch]);

  // Validate columns on mount - warn about invalid operator/dataType combinations
  useEffect(() => {
    validateColumnsOperators(columns);
  }, [columns]);

  // Wrap hydration callbacks to filter out invalid filters
  const handleHydrateFilters = useCallback(
    (filters: FilterValue[]) => {
      const validFilters = dropBannedFilterValues(
        filterValidFilters(filters, columns),
        droppedFilterValues
      );
      actions.setFilters(validFilters);
    },
    [columns, actions, droppedFilterValues]
  );

  // localStorage hydration sets the whole state at once; drop invalid filters
  // (bad operator/column or non-[min,max] NUMBER range) so they can't 400 the table.
  const handleHydrateState = useCallback(
    (hydratedState: TableQueryState) => {
      const hydrated = hydrateWithValidFilters(hydratedState, columns);
      actions.setState({
        ...hydrated,
        filters: dropBannedFilterValues(hydrated.filters, droppedFilterValues),
      });
    },
    [columns, actions, droppedFilterValues]
  );

  const validSortFields = useMemo(
    () => extractValidSortFields(columns),
    [columns]
  );
  useDataTableLocalPreferences({
    tableId: id,
    state,
    onHydrateState: handleHydrateState,
    enabled: enableLocalPreferences,
    validSortFields,
  });
  const { requestExternalKeyClear } = useDataTableStateEncoding({
    tableId: id,
    filters: activeFilters,
    sort: state.sort,
    search: state.search,
    expandedRowIds: state.expandedRowIds,
    columns,
    onHydrateFilters: handleHydrateFilters,
    onHydrateSort: actions.setSort,
    onHydrateSearch: actions.setSearch,
    onHydrateExpanded: actions.setExpandedRowIds,
    enabled: enableUrlState,
    externalUrlKeys: clearFiltersUrlKeys,
  });

  const {
    data,
    pagination: paginationMeta,
    isLoading,
    isShowingPlaceholder,
    isQueryFetching,
    isError,
    error,
  } = useDataTableQuery<TRow, TMeta>({
    getQueryKeysFromParams,
    queryFn,
    queryConfig,
    onMeta,
    countScope,
    pagination: state.pagination,
    filters: activeFilters,
    sort: state.sort,
    search: querySearch,
  });
  // A mark or allow can empty the page the admin is sitting on. pageResetKey
  // only moves when the pill changes, so clamp here once the count is known.
  // total === 0 is left alone: several tables report 0 while a page index is
  // still set, and snapping those would drop a page the user has not left.
  // Wait out a request for this page: a cached total can still be smaller,
  // and clamping on it throws the target page's render away before its fetch
  // commits — which is why this needs `isQueryFetching` (any in-flight
  // request) and not just the placeholder flag.
  const pageSize = state.pagination.pageSize;
  const totalRows = paginationMeta?.total;
  if (
    !isLoading &&
    !isShowingPlaceholder &&
    !isQueryFetching &&
    typeof totalRows === 'number' &&
    totalRows > 0 &&
    pageSize > 0
  ) {
    const lastPageIndex = Math.ceil(totalRows / pageSize) - 1;
    if (state.pagination.pageIndex > lastPageIndex) {
      actions.setPage(lastPageIndex);
    }
  }
  // Deselect everything: exit "select all across pages" and drop the selection
  // (keys mode) or the exclusion set (select-all mode). Stable so it can back
  // both the bulk bar's "Clear Selection" AND the header checkbox in select-all
  // mode, where the header is a GLOBAL toggle rather than a per-page one.
  const clearSelection = useCallback(() => {
    setSelectAllAcrossPages(false);
    setRowSelection({});
  }, []);

  const table = useDataTableInstance<TRow>({
    data,
    columns: renderColumns,
    pagination: state.pagination,
    paginationMeta,
    enableRowSelection,
    selectAllAcrossPages,
    // KIR-4107: key rows by stable identity, not array index. Fall back to the
    // expansion getRowId when selection isn't configured, so a live-polling
    // table (Running Now) that gains/loses/reorders rows moves the actual row
    // instead of rewriting each position's content — which read as the list
    // "jumping" under the user.
    getRowId: rowSelectionOptions?.getRowId ?? rowExpansion?.getRowId,
    rowSelection,
    onRowSelectionChange: setRowSelection,
    // In select-all mode the header checkbox spans ALL pages, so clicking it
    // clears the whole cross-page selection instead of only excluding the
    // current page (which left other pages selected — KIR-3252 bug).
    onSelectAllModeToggle: clearSelection,
    getRowAriaLabel: rowSelectionOptions?.getRowAriaLabel,
    enableColumnResizing: resizableColumns,
    columnSizing: columnCustomization
      ? (columnLayout?.widths ?? NO_WIDTHS)
      : undefined,
    onColumnSizingChange: columnCustomization
      ? handleColumnSizingChange
      : undefined,
  });

  // Full reset — a filter / search / external-token (e.g. the Alerts visibility
  // tab) change is a brand-new result set, so it invalidates ANY selection: both
  // a page-level (keys-mode) selection and a "select all matching across pages"
  // selection. Done during render via the "store previous key" pattern (React's
  // alternative to a setState-in-effect), guarded by `enableRowSelection`.
  const selectionResetToken = rowSelectionOptions?.resetToken;
  const selectionResetKey = useMemo(
    () => JSON.stringify([state.filters, querySearch, selectionResetToken]),
    [state.filters, querySearch, selectionResetToken]
  );
  const [prevSelectionResetKey, setPrevSelectionResetKey] =
    useState(selectionResetKey);
  const fullResetTriggered =
    enableRowSelection && prevSelectionResetKey !== selectionResetKey;
  if (fullResetTriggered) {
    setPrevSelectionResetKey(selectionResetKey);
    if (Object.keys(rowSelection).length > 0) {
      setRowSelection({});
    }
    if (selectAllAcrossPages) {
      setSelectAllAcrossPages(false);
    }
  }

  // Pagination clears ONLY a page-level (keys-mode) selection — per ticket FE#4
  // checkbox picks are per-page. "Select all matching across pages" is
  // query-scoped and spans pages by design (the user paginates to deselect rows
  // into the exclusion set, and the bar sends `excludeKeys`), so paginating must
  // NOT exit that mode or wipe its exclusions.
  const paginationKey = useMemo(
    () =>
      JSON.stringify([state.pagination.pageIndex, state.pagination.pageSize]),
    [state.pagination.pageIndex, state.pagination.pageSize]
  );
  const [prevPaginationKey, setPrevPaginationKey] = useState(paginationKey);
  const paginationChanged =
    enableRowSelection && prevPaginationKey !== paginationKey;
  if (paginationChanged) {
    setPrevPaginationKey(paginationKey);
    if (!selectAllAcrossPages && Object.keys(rowSelection).length > 0) {
      setRowSelection({});
    }
  }

  // Reconcile the selection against the freshly-fetched page: clear it over an
  // empty result set (e.g. the last matching alert was excluded out of view),
  // and prune orphaned keys a bulk action moved off the page so the bar's count
  // stays truthful. Pure logic lives in `reconcileSelectionWithData` (unit
  // tested); here we only apply the deltas it reports, keeping the render-time
  // update self-terminating (each field is `null` when unchanged).
  //
  // Skip the render where a reset just fired (full or pagination): the block(s)
  // above already adjusted the selection, and reconciling the pre-change
  // selection in the SAME render would fight that (the later `setRowSelection`
  // wins). The next render reconciles against the fetched data.
  const selectionKeyChanged = fullResetTriggered || paginationChanged;
  const reconcileGetRowId = rowSelectionOptions?.getRowId;
  const reconciled = selectionKeyChanged
    ? { rowSelection: null, selectAllAcrossPages: null }
    : reconcileSelectionWithData({
        enableRowSelection,
        isShowingPlaceholder,
        total: paginationMeta.total,
        selectAllAcrossPages,
        rowSelection,
        pageRowIds: reconcileGetRowId ? data.map(reconcileGetRowId) : [],
      });
  if (reconciled.selectAllAcrossPages !== null) {
    setSelectAllAcrossPages(reconciled.selectAllAcrossPages);
  }
  if (reconciled.rowSelection !== null) {
    setRowSelection(reconciled.rowSelection);
  }

  // Bulk-toolbar context: the engine's single egress for selection + query
  // state, derived directly from the controlled `rowSelection` (so the memo
  // recomputes as selection changes). Only assembled when selection is enabled.
  const bulkToolbar = rowSelectionOptions?.renderBulkToolbar;
  const hangBulkToolbar = rowSelectionOptions?.hangBulkToolbar ?? false;
  const getRowId = rowSelectionOptions?.getRowId;
  // Grouped columns carry their filters on the leaves, so flatten before
  // asking — a table whose only filterable fields sit under a group must still
  // offer "Clear filters".
  const hasFilterableColumns = useMemo(
    () => getLeafColumns(columns).some(column => column.filterable),
    [columns]
  );

  const handleClearFilters = useCallback((): void => {
    // Armed BEFORE the state change, so the URL sync this triggers drops the
    // caller's own filter params in the same write (KIR-5088). Without it the
    // caller's `onClearFilters` and that sync both write the search params in
    // one tick, the sync wins from a pre-delete snapshot, and the caller's
    // filter silently survives a "Clear filters" that cleared everything else.
    requestExternalKeyClear();
    actions.clearFilters();
    onClearFilters?.();
  }, [actions, onClearFilters, requestExternalKeyClear]);

  const bulkToolbarNode = useMemo(() => {
    if (!bulkToolbar || !getRowId) return null;
    const isMarked = (id: string): boolean => rowSelection[id] === true;
    const pageRowIds = data.map(getRowId);
    // `rowSelection` keys mean INCLUDED in keys mode, EXCLUDED in select-all mode.
    const markedIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    // Effectively-selected current-page rows (inverted in select-all mode).
    const selectedRows = data.filter(row => {
      const marked = isMarked(getRowId(row));
      return selectAllAcrossPages ? !marked : marked;
    });
    const allPageMarked = pageRowIds.length > 0 && pageRowIds.every(isMarked);
    const ctx: BulkToolbarContext<TRow> = {
      selectedRowIds: selectAllAcrossPages ? [] : markedIds,
      selectedRows,
      pageRowIds,
      clearSelection,
      selectAllAcrossPages,
      excludedRowIds: selectAllAcrossPages ? markedIds : [],
      enterSelectAllAcrossPages: () => {
        setSelectAllAcrossPages(true);
        setRowSelection({});
      },
      // Page-scoped header state (keys-mode semantics; the bar reads these only
      // outside select-all mode to decide whether to offer "select all").
      isAllPageSelected: allPageMarked,
      isSomePageSelected: pageRowIds.some(isMarked) && !allPageMarked,
      filters: activeFilters,
      search: querySearch ?? '',
      total: paginationMeta.total,
      pageCount: Math.ceil(paginationMeta.total / paginationMeta.limit) || 1,
      pageSize: state.pagination.pageSize,
    };
    return bulkToolbar(ctx);
  }, [
    bulkToolbar,
    getRowId,
    rowSelection,
    selectAllAcrossPages,
    clearSelection,
    data,
    activeFilters,
    querySearch,
    paginationMeta.total,
    paginationMeta.limit,
    state.pagination.pageSize,
  ]);

  const customizableColumns = useMemo(
    () => getCustomizableColumns(columns),
    [columns]
  );
  const currentColumnOrder = useMemo(
    () =>
      resolveColumnOrder(
        customizableColumns.map(column => column.id),
        columnLayout?.order ?? []
      ),
    [customizableColumns, columnLayout]
  );
  const columnsPanelItems = useMemo<ColumnsPanelItem[]>(() => {
    const rendered = new Set(renderColumns.map(column => column.id));
    const byId = new Map(
      customizableColumns.map(column => [column.id, column])
    );
    const savedHidden = new Set(columnLayout?.hidden ?? []);
    const inFallback = currentColumnOrder.every(id => savedHidden.has(id));
    return currentColumnOrder.map(id => ({
      id,
      label: byId.get(id)?.header ?? id,
      visible: rendered.has(id),
      // Hiding this one would leave the saved layout hiding every column
      // (e.g. the only other shown column is just force-shown by a filter).
      // In the all-hidden fallback the toggle rebuilds instead, so allow it.
      hideDisabled:
        !inFallback &&
        currentColumnOrder.every(
          otherId => otherId === id || savedHidden.has(otherId)
        ),
    }));
  }, [customizableColumns, currentColumnOrder, renderColumns, columnLayout]);
  const moveColumn = useCallback(
    (from: number, to: number): void =>
      updateLayout({
        order: mergeColumnOrder(
          columnLayout?.order ?? [],
          moveItem(currentColumnOrder, from, to)
        ),
      }),
    [updateLayout, columnLayout, currentColumnOrder]
  );
  // Header drag: the dragged column takes the target's slot; hidden columns
  // between them stay put — the same move the panel makes.
  const handleColumnMove = useCallback(
    (fromId: string, toId: string): void => {
      const from = currentColumnOrder.indexOf(fromId);
      const to = currentColumnOrder.indexOf(toId);
      if (from !== -1 && to !== -1) moveColumn(from, to);
    },
    [currentColumnOrder, moveColumn]
  );
  const handleToggleColumn = useCallback(
    (id: string, visible: boolean): void => {
      if (visible) {
        updateLayout({
          hidden: (columnLayout?.hidden ?? []).filter(
            hiddenId => hiddenId !== id
          ),
        });
        return;
      }
      // Hiding a column drops its filter and sort with it — otherwise they would
      // keep applying (and force the column back into view).
      const column = columns.find(candidate => candidate.id === id);
      // KIR-5098: including the column's alternate field (e.g. Count range).
      const alternateField =
        column?.filterable && column.alternateFilter
          ? column.alternateFilter.field
          : undefined;
      const isOwnFilter = (field: string): boolean =>
        field === id || field === alternateField;
      if (state.filters.some(filter => isOwnFilter(filter.field))) {
        actions.setFilters(
          state.filters.filter(filter => !isOwnFilter(filter.field))
        );
      }
      if (column && state.sort?.field === (column.sortField ?? column.id)) {
        actions.clearSort();
      }
      const savedHidden = columnLayout?.hidden ?? [];
      const ownIds = customizableColumns.map(candidate => candidate.id);
      // A layout that hides every column this table has (possible when it is
      // shared with a table that has other columns) renders all of them as a
      // fallback; appending would change nothing. Rebuild this table's share
      // of `hidden` so exactly this column goes away.
      const inFallback = ownIds.every(ownId => savedHidden.includes(ownId));
      const hidden = inFallback
        ? [...savedHidden.filter(hiddenId => !ownIds.includes(hiddenId)), id]
        : [...new Set([...savedHidden, id])];
      updateLayout({ hidden });
    },
    [
      updateLayout,
      columnLayout,
      state.filters,
      state.sort,
      actions,
      columns,
      customizableColumns,
    ]
  );
  const columnsControl = columnCustomization ? (
    <ColumnsPanel
      items={columnsPanelItems}
      lockedLabels={enableRowSelection ? ['Row selector'] : []}
      onToggle={handleToggleColumn}
      onMove={moveColumn}
      onReset={() => onLayoutChange?.(DEFAULT_COLUMN_LAYOUT)}
    />
  ) : null;
  // Widths change the table's scroll extent without resizing the container,
  // so they must re-trigger the overflow measure too.
  const overflowRecomputeKey = [
    ...renderColumns.map(column => column.id),
    JSON.stringify(columnLayout?.widths ?? NO_WIDTHS),
  ].join(',');
  const columnSizes = table.getVisibleLeafColumns().map(readColumnSize);
  const extraColumnWidth = !enableRowExpansion
    ? 0
    : rowExpansion?.toggleAddon
      ? EXPANSION_COLUMN_WITH_ADDON_WIDTH
      : EXPANSION_COLUMN_WIDTH;
  // A resizable table without a fill column is as wide as its columns.
  // Stretched to the container, every column would render wider than the
  // width a resize starts from and saves, and narrowing one would widen the
  // rest.
  const exactTableWidth =
    resizableColumns && !columnSizes.some(size => size.fill)
      ? columnSizes.reduce((sum, size) => sum + size.size, extraColumnWidth)
      : undefined;

  const scrollContainer = (
    <div
      ref={scrollContainerRef}
      className={cn(
        'relative isolate flex-1 overflow-auto',
        !showCardWrapper && 'rounded-md border',
        containerClassName
      )}
    >
      <Table
        addParentWrapper={false}
        className={cn(
          'text-xs transition-opacity duration-200',
          exactTableWidth === undefined && 'min-w-full',
          tableLayout === 'auto' ? 'table-auto' : 'table-fixed',
          isShowingPlaceholder && 'opacity-50 pointer-events-none',
          tableClassName
        )}
        style={
          tableLayout === 'fixed'
            ? {
                width: exactTableWidth,
                minWidth: getFixedLayoutMinWidth(columnSizes, extraColumnWidth),
              }
            : undefined
        }
      >
        {/* Authoritative column widths so grouped headers don't split a span
            50/50 under table-fixed. Omitted in `auto`, which sizes columns
            from their min/max instead. */}
        {tableLayout === 'fixed' && (
          <colgroup>
            {enableRowExpansion && (
              <col className={rowExpansion?.toggleAddon ? 'w-20' : 'w-10'} />
            )}
            {table.getVisibleLeafColumns().map(column => (
              <col
                key={column.id}
                style={
                  isFillColumn(column.columnDef.meta)
                    ? undefined
                    : { width: column.getSize() }
                }
              />
            ))}
          </colgroup>
        )}
        <TableHeader
          table={table}
          columns={renderColumns}
          filters={state.filters}
          onFilterChange={actions.setFilter}
          sort={state.sort}
          onSortChange={actions.toggleSort}
          enableSorting={enableSorting}
          enableRowExpansion={enableRowExpansion}
          tableLayout={tableLayout}
          onColumnMove={resizableColumns ? handleColumnMove : undefined}
        />
        <TableBody
          table={table}
          isLoading={isLoading}
          isError={isError}
          error={error}
          noDataLabel={noDataLabel}
          enableRowExpansion={enableRowExpansion}
          rowExpansion={rowExpansion}
          onRowClick={onRowClick}
          getRowClassName={getRowClassName}
          searchValue={state.search}
          activeFilterCount={activeFilters.length}
          expandedRowIds={state.expandedRowIds}
          onToggleRowExpansion={actions.toggleRowExpansion}
          tableLayout={tableLayout}
        />
      </Table>
    </div>
  );

  return (
    <DataTableSearchContext.Provider value={normalizedSearch}>
      <div
        className={cn(
          'relative flex flex-col',
          showCardWrapper &&
            'rounded-md border border-table-border shadow-card overflow-hidden',
          !showCardWrapper && showShadow && 'shadow-card',
          className
        )}
      >
        {showToolbar && (
          <DataTableToolbar
            onClearFilters={handleClearFilters}
            searchValue={state.search}
            onSearchChange={actions.setSearch}
            searchConfig={search}
            hasActiveFilters={activeFilters.length > 0}
            hasFilterableColumns={hasFilterableColumns}
            endSlot={toolbarEndSlot}
            columnsControl={columnsControl}
          />
        )}
        {/* Positioning context for the right-edge sign, outside the scroller
            so the sign stays pinned while the table scrolls under it. Always
            rendered (`contents` = no box when unused) so turning customization
            on never remounts the table subtree or resets its scroll. */}
        <div
          className={
            columnCustomization
              ? 'relative flex min-h-0 flex-1 flex-col'
              : 'contents'
          }
        >
          {scrollContainer}
          {columnCustomization && (
            <OverflowIndicator
              containerRef={scrollContainerRef}
              recomputeKey={overflowRecomputeKey}
            />
          )}
        </div>
        <TableFooter
          table={table}
          paginationState={state.pagination}
          pageSizeOptions={pageSizeOptions}
          onPageChange={actions.setPage}
          onPageSizeChange={actions.setPageSize}
        />
      </div>
      {/* Bulk-action bar (KIR-3252): outside the table's border, between it
          and the app's "Contact us" footer, so it never covers a row or the
          pagination. Laid out after the table and sticky above that footer,
          so a table taller than the screen keeps it in view; an
          overflow-hidden ancestor would measure that offset from its own
          bottom instead (use overflow-clip). With `hangBulkToolbar` it hangs
          from a zero-height anchor instead, so a table filling a fixed box
          never shrinks. Hidden while the consumer renders nothing. */}
      {bulkToolbar &&
        (hangBulkToolbar ? (
          <div className="relative h-0">
            <div
              data-testid="bulk-action-bar"
              // z-40: a bar that wraps past the room under the table covers
              // the z-30 "Contact us" footer instead of going behind it.
              className={cn('absolute inset-x-0 top-0.5 z-40', BULK_BAR_CLASS)}
            >
              {bulkToolbarNode}
            </div>
          </div>
        ) : (
          <div
            data-testid="bulk-action-bar"
            className={cn(STICKY_ABOVE_APP_FOOTER, 'z-30 mt-2', BULK_BAR_CLASS)}
          >
            {bulkToolbarNode}
          </div>
        ))}
    </DataTableSearchContext.Provider>
  );
}

export function DataTable<
  TRow extends TableRowData,
  TMeta extends ResponseMeta = ResponseMeta,
>({
  errorBoundaryFallback,
  errorBoundaryLabel,
  ...props
}: DataTableProps<TRow, TMeta>) {
  const fallbackElement = errorBoundaryFallback ?? (
    <ErrorFallback
      label={errorBoundaryLabel ?? 'Something went wrong loading the table.'}
    />
  );

  return (
    <Sentry.ErrorBoundary fallback={fallbackElement}>
      <DataTableInner<TRow, TMeta> {...props} />
    </Sentry.ErrorBoundary>
  );
}
